import React, { useLayoutEffect, useState } from 'react';

import { AlineTransforms } from '@aline/common';
import { DialogActions, DialogContent, InputLabel } from '@mui/material';
import { equals, keys } from 'ramda';
import { AlineInput } from 'src/AlineInput';
import {
  AlineButton,
  AlineDialog,
  CheckMarkMenuItem,
  ToolbarInput,
  ToolbarSelect,
} from 'src/components';
import { ArrowDownIcon } from 'src/components/icons/ArrowDownIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { parseValue } from 'src/editor/toolbars/tabs/layout-tab/utils';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  indentationFields: {
    display: 'flex',
  },
  input: {
    height: 32,
    width: 72,
  },
  inputWrapper: {
    '&:first-child': {
      marginTop: 0,
    },
    marginTop: theme.spacing(2),
  },
  label: {
    lineHeight: `calc(${theme.spacing(4)} + 2px)`,
  },
  labelWrapper: {
    '&:first-child': {
      marginTop: 0,
    },
    display: 'flex',
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  specialIndentation: {
    marginLeft: theme.spacing(2),
  },
}));

function getSpecialIndentation(firstLine: number): string {
  if (firstLine === 0) {
    return 'None';
  } else if (firstLine > 0) {
    return 'First line';
  }
  return 'Hanging';
}

interface ParagraphProperties {
  indentationFirstLine: number;
  indentationLeft: number;
  indentationRight: number;
}

export function IndentationOptionsDialog(props: {
  onClose: () => void;
  open: boolean;
}): JSX.Element {
  const editor = useEditor();
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const indentationProperties = useEditorTabStateSelector(
    state => state.derived.indentationProperties,
    equals
  );

  const [specialOpen, setSpecialOpen] = useState<boolean>(false);
  const [indentationLeft, setIndentationLeft] = useState<string>(
    indentationProperties.left.toString()
  );
  const [indentationRight, setIndentationRight] = useState<string>(
    indentationProperties.right.toString()
  );
  const [specialIndentationOption, setSpecialIndentationOption] = useState(
    getSpecialIndentation(indentationProperties.firstLine)
  );
  const [specialIndentation, setSpecialIndentation] = useState(
    specialIndentationOption === 'None' ? '' : Math.abs(indentationProperties.firstLine).toString()
  );

  function resetState(): void {
    setIndentationLeft(indentationProperties.left.toString());
    setIndentationRight(indentationProperties.right.toString());

    const newSpecialIndentationOption = getSpecialIndentation(indentationProperties.firstLine);
    setSpecialIndentationOption(newSpecialIndentationOption);
    setSpecialIndentation(
      newSpecialIndentationOption === 'None'
        ? ''
        : Math.abs(indentationProperties.firstLine).toString()
    );
  }

  useLayoutEffect(() => {
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indentationProperties]);

  return (
    <AlineDialog open={props.open} title="Indentation options" onClose={props.onClose}>
      <DialogContent>
        <div className={classes.indentationFields}>
          <div>
            <div className={classes.labelWrapper}>
              <InputLabel className={classes.label} htmlFor="indentation-options-left">
                Left
              </InputLabel>
            </div>
            <div className={classes.labelWrapper}>
              <InputLabel className={classes.label} htmlFor="indentation-options-right">
                Right
              </InputLabel>
            </div>
            <div className={classes.labelWrapper}>
              <InputLabel className={classes.label} htmlFor="indentation-options-special">
                Special
              </InputLabel>
              <div className={classes.specialIndentation}>
                <ToolbarSelect
                  IconComponent={(): JSX.Element => (
                    <ArrowDownIcon iconStyles={{ height: 12, right: 9, width: 12 }} />
                  )}
                  input={
                    <ToolbarInput
                      inputStyles={{ height: 34, margin: 0, width: 120 }}
                      open={specialOpen}
                    />
                  }
                  menuAttribute="indentation-options-dialog-special-select-menu"
                  open={specialOpen}
                  selectAttribute="indentation-options-dialog-special-select-select"
                  spanAttribute="indentation-options-dialog-special-select-span"
                  value={specialIndentationOption}
                  onClose={(): void => setSpecialOpen(false)}
                  onOpen={(): void => setSpecialOpen(true)}
                  onSelectChange={(event): void => {
                    const option = event.target.value as string;
                    setSpecialIndentationOption(option);
                    if (option === 'None') {
                      setSpecialIndentation('');
                    }
                  }}
                >
                  <CheckMarkMenuItem selected={specialIndentationOption === 'None'} value="None">
                    None
                  </CheckMarkMenuItem>
                  <CheckMarkMenuItem
                    selected={specialIndentationOption === 'First line'}
                    value="First line"
                  >
                    First line
                  </CheckMarkMenuItem>
                  <CheckMarkMenuItem
                    selected={specialIndentationOption === 'Hanging'}
                    value="Hanging"
                  >
                    Hanging
                  </CheckMarkMenuItem>
                </ToolbarSelect>
              </div>
            </div>
          </div>
          <div>
            <div className={classes.inputWrapper}>
              <AlineInput
                id="indentation-options-left"
                inputClassName={classes.input}
                value={indentationLeft}
                onChange={(event): void => setIndentationLeft(event.target.value)}
              />
            </div>
            <div className={classes.inputWrapper}>
              <AlineInput
                id="indentation-options-right"
                inputClassName={classes.input}
                value={indentationRight}
                onChange={(event): void => setIndentationRight(event.target.value)}
              />
            </div>
            <div className={classes.inputWrapper}>
              <AlineInput
                id="indentation-options-special"
                inputClassName={classes.input}
                value={specialIndentation}
                onChange={(event): void => {
                  setSpecialIndentation(event.target.value);
                  if (specialIndentationOption === 'None') {
                    setSpecialIndentationOption('First line');
                  }
                }}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={props.onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          variant="contained"
          onClick={(): void => {
            if (!currentUserId || !editingMode) return;

            let left = parseValue(indentationLeft) ?? indentationProperties.left;
            let firstLine = 0;
            if (['First line', 'Hanging'].includes(specialIndentationOption)) {
              firstLine = parseValue(specialIndentation) || 0;
            }
            if (specialIndentationOption === 'Hanging') {
              firstLine = firstLine * -1;
              left = left + (firstLine - indentationProperties.firstLine) * -1;
            }

            resetState();

            const paragraphProperties: ParagraphProperties = {
              indentationFirstLine: firstLine,
              indentationLeft: left,
              indentationRight: parseValue(indentationRight) ?? indentationProperties.right,
            };

            AlineTransforms.withoutNormalizing(editor, () => {
              const propKeys = keys(paragraphProperties);
              for (const propKey of propKeys) {
                AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
                  [propKey]: paragraphProperties[propKey],
                });
              }
            });
            props.onClose();
          }}
        >
          Apply
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}
